import { ChatCircleTextIcon, MapTrifoldIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { exitReasonKey, type SelectionState } from '@entities/selection';

import styles from './ExhaustedState.module.css';

interface ExhaustedStateProps {
  selection: SelectionState;
}

/**
 * «Ни один из N проверенных вариантов не подошёл» (FE-222, MVP_PRD §34.6).
 *
 * **Исчерпание — штатный исход, а не ошибка.** Экран не извиняется и не
 * притворяется сбоем: он показывает, сколько автомобилей проверено, что
 * произошло с каждым, и два конкретных предложения — изменить критерии
 * (бесплатно, §35.4) или расширить географию (§43.3).
 *
 * Разбор строится по кодам причин выбытия; кода без перевода на экране нет.
 */
export function ExhaustedState({ selection }: ExhaustedStateProps) {
  const { t, i18n } = useTranslation();

  // Сколько раз сработала каждая причина: перечислять двенадцать строк подряд
  // бессмысленно, а сгруппированный разбор читается.
  const byReason = new Map<string, number>();
  for (const slot of selection.history) {
    if (slot.exit_reason === null) continue;
    byReason.set(slot.exit_reason, (byReason.get(slot.exit_reason) ?? 0) + 1);
  }

  return (
    <section className={styles.card} aria-labelledby="exhausted-title">
      <h2 id="exhausted-title" className={styles.title}>
        {t('selection.exhausted.title', { count: selection.checked_vehicle_count })}
      </h2>
      <p className={styles.text}>{t('selection.exhausted.text')}</p>

      {byReason.size === 0 ? null : (
        <dl className={styles.breakdown}>
          {[...byReason.entries()].map(([reason, count]) => {
            const key = exitReasonKey(reason as never);
            if (!i18n.exists(key)) return null;
            return (
              <div key={reason} className={styles.row}>
                <dt>{t(key)}</dt>
                <dd className="tabular">{count}</dd>
              </div>
            );
          })}
        </dl>
      )}

      <div className={styles.actions}>
        {/* Кнопки «изменить критерии» нет: диалог с Karl стоит рядом, в рельсе,
            и уточнение оттуда пересобирает подбор целиком (FE-193). */}
        <p className={styles.geo}>
          <ChatCircleTextIcon size={16} aria-hidden="true" />
          {t('selection.exhausted.editCriteria')}
        </p>
        {/* Расширение географии — отдельный контур (Epic 32). Кнопки, которая
            ничего не делает, здесь нет: вместо неё — объяснение, как расширить
            поиск через диалог с Karl (MVP_PRD §2). */}
        <p className={styles.geo}>
          <MapTrifoldIcon size={16} aria-hidden="true" />
          {t('selection.exhausted.geography')}
        </p>
      </div>
    </section>
  );
}
